import { useState } from "react";
import { Modal, Input, DatePicker, Switch, Radio, Select, Upload } from "antd";
import { Button1 } from "../index";
import uploadIcon from "../../assets/icons/upload-icon.png";

const LABEL_GRAY = "#7D7D7D";
const BRAND_GREEN = "#1BBB6B";

const CreateTransactionModal = ({ open, onClose, onCreate }) => {
  const [advanced, setAdvanced] = useState(false);
  const [form, setForm] = useState({
    title: "",
    transactionDate: null,
    transactionType: "Deposit",
    amount: "",
    currency: "AUD",
    // Advanced
    transferredFromAccount: "Saving Account",
    transferToAccount: "Checking",
    category: "Unassigned",
    attachImage: null,
    notes: "",
    repeatEvery: false,
    repeatCount: 1,
    repeatUnit: "Month",
    repeatDay: "Weekend",
    repeatStarting: null,
    repeatEndType: "Until",
    repeatEndDate: null,
    remindMe: true,
    emailMe: true,
    daysBefore: 2,
    markAsCompleted: false,
  });

  const update = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleCreate = () => {
    onCreate?.(form);
    onClose?.();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      title={null}
      closable={true}
    >
      <div className="d-flex flex-column gap-3 p-2">
        <div className="d-flex justify-content-between align-items-center">
          <span className="fs-5 fw-semibold">Create New Transaction</span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span className="fs12px" style={{ color: LABEL_GRAY }}>
            Advanced view
          </span>
          <Switch checked={advanced} onChange={setAdvanced} />
        </div>

        <div className="d-flex flex-column gap-2">
          <label className="fs12px fw-medium">Title</label>
          <Input
            placeholder="Input title"
            className="p-2"
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
          />
        </div>

        <div className="row g-2">
          <div className="col-md-6">
            <label className="fs12px fw-medium">Transaction Date</label>
            <DatePicker
              className="p-2 w-100"
              placeholder="Select date"
              onChange={(d) => update("transactionDate", d)}
            />
          </div>
          <div className="col-md-6">
            <label className="fs12px fw-medium">Transaction Type</label>
            <div className="d-flex gap-3 mt-2">
              <Radio
                checked={form.transactionType === "Deposit"}
                onChange={() => update("transactionType", "Deposit")}
                style={{ color: BRAND_GREEN }}
              >
                Deposit
              </Radio>
              <Radio
                checked={form.transactionType === "Withdrawal"}
                onChange={() => update("transactionType", "Withdrawal")}
              >
                Withdrawal
              </Radio>
              <Radio
                checked={form.transactionType === "Transfer"}
                onChange={() => update("transactionType", "Transfer")}
              >
                Transfer
              </Radio>
            </div>
          </div>
        </div>

        <div className="row g-2">
          <div className="col-md-9">
            <label className="fs12px fw-medium">Amount</label>
            <Input
              placeholder="Input amount"
              className="p-2"
              value={form.amount}
              onChange={(e) => update("amount", e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="fs12px fw-medium">Currency</label>
            <Select
              className="w-100"
              value={form.currency}
              onChange={(v) => update("currency", v)}
              options={[
                { label: "AUD", value: "AUD" },
                { label: "USD", value: "USD" },
                { label: "EUR", value: "EUR" },
                { label: "GBP", value: "GBP" },
              ]}
            />
          </div>
        </div>

        {advanced && (
          <>
            <div className="d-flex flex-column gap-2">
              <label className="fs12px fw-medium">
                Transferred from account
              </label>
              <Select
                className="w-100"
                value={form.transferredFromAccount}
                onChange={(v) => update("transferredFromAccount", v)}
                options={[
                  { label: "Saving Account", value: "Saving Account" },
                  { label: "Checking", value: "Checking" },
                  { label: "Investment", value: "Investment" },
                ]}
              />
            </div>

            <div className="row g-2">
              <div className="col-md-6">
                <label className="fs12px fw-medium">Transfer to account</label>
                <Select
                  className="w-100"
                  value={form.transferToAccount}
                  onChange={(v) => update("transferToAccount", v)}
                  options={[
                    { label: "Checking", value: "Checking" },
                    { label: "Saving Account", value: "Saving Account" },
                  ]}
                />
              </div>
              <div className="col-md-6">
                <label className="fs12px fw-medium">Category</label>
                <Select
                  className="w-100"
                  value={form.category}
                  onChange={(v) => update("category", v)}
                  options={[
                    { label: "Unassigned", value: "Unassigned" },
                    { label: "Groceries", value: "Groceries" },
                    { label: "Utilities", value: "Utilities" },
                    { label: "Rent", value: "Rent" },
                    { label: "Salary", value: "Salary" },
                  ]}
                />
              </div>
            </div>

            <div className="row g-2">
              <div className="col-md-6">
                <label className="fs12px fw-medium">Attach image</label>
                <Upload
                  maxCount={1}
                  showUploadList={false}
                  beforeUpload={(file) => {
                    update("attachImage", file);
                    return false;
                  }}
                >
                  <Input
                    className="p-2"
                    placeholder="Choose file"
                    value={form.attachImage?.name || ""}
                    readOnly
                    suffix={<img src={uploadIcon} alt="upload" />}
                  />
                </Upload>
              </div>
              <div className="col-md-6">
                <label className="fs12px fw-medium">Add notes</label>
                <Input
                  className="p-2"
                  placeholder="Input notes"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                />
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <input
                type="checkbox"
                checked={form.repeatEvery}
                onChange={(e) => update("repeatEvery", e.target.checked)}
              />
              <span className="fs12px fw-medium">Repeat every</span>
              <Input
                style={{ width: 60 }}
                className="p-1"
                value={form.repeatCount}
                onChange={(e) => update("repeatCount", e.target.value)}
              />
              <Select
                style={{ width: 120 }}
                value={form.repeatUnit}
                onChange={(v) => update("repeatUnit", v)}
                options={[
                  { label: "Day", value: "Day" },
                  { label: "Week", value: "Week" },
                  { label: "Month", value: "Month" },
                  { label: "Year", value: "Year" },
                ]}
              />
              <Select
                style={{ width: 140 }}
                value={form.repeatDay}
                onChange={(v) => update("repeatDay", v)}
                options={[
                  { label: "Weekend", value: "Weekend" },
                  { label: "Weekday", value: "Weekday" },
                  { label: "Any Day", value: "AnyDay" },
                ]}
              />
            </div>

            <div className="row g-2">
              <div className="col-md-4">
                <label className="fs12px fw-medium">Repeat Starting</label>
                <DatePicker
                  className="p-2 w-100"
                  onChange={(d) => update("repeatStarting", d)}
                />
              </div>
              <div className="col-md-4">
                <Select
                  className="w-100 mt-4"
                  value={form.repeatEndType}
                  onChange={(v) => update("repeatEndType", v)}
                  options={[
                    { label: "Until", value: "Until" },
                    { label: "For", value: "For" },
                    { label: "Forever", value: "Forever" },
                  ]}
                />
              </div>
              <div className="col-md-4">
                <DatePicker
                  className="p-2 w-100 mt-4"
                  onChange={(d) => update("repeatEndDate", d)}
                />
              </div>
            </div>

            <div className="d-flex flex-wrap align-items-center gap-3 mt-1">
              <label className="d-flex align-items-center gap-2 mb-0">
                <input
                  type="checkbox"
                  checked={form.remindMe}
                  onChange={(e) => update("remindMe", e.target.checked)}
                />
                <span className="fs12px fw-medium">Remind me</span>
              </label>
              <label className="d-flex align-items-center gap-2 mb-0">
                <input
                  type="checkbox"
                  checked={form.emailMe}
                  onChange={(e) => update("emailMe", e.target.checked)}
                />
                <span className="fs12px fw-medium">Email me</span>
              </label>
              <div className="d-flex align-items-center gap-1">
                <Input
                  style={{ width: 50 }}
                  className="p-1"
                  value={form.daysBefore}
                  onChange={(e) => update("daysBefore", e.target.value)}
                />
                <span className="fs12px">days before.</span>
              </div>
              <label className="d-flex align-items-center gap-2 mb-0">
                <input
                  type="checkbox"
                  checked={form.markAsCompleted}
                  onChange={(e) => update("markAsCompleted", e.target.checked)}
                />
                <span className="fs12px fw-medium">Mark as completed</span>
              </label>
            </div>
          </>
        )}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button1
            buttonName="Cancel"
            className="btn-outline-danger"
            onClick={onClose}
          />
          <Button1
            buttonName="Create New"
            className="btn-dark"
            onClick={handleCreate}
          />
        </div>
      </div>
    </Modal>
  );
};

export default CreateTransactionModal;
